import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Star, ArrowRight, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { sampleListings } from "@/data/sample-listings";
import { useState, useRef, useEffect } from "react";

const featured360 = sampleListings.filter(l => l.featured && l.tourUrl).slice(0, 3);

const LazyTourCard = ({ listing, index }: { listing: typeof featured360[0]; index: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.15 }}
      className="group"
    >
      <div className="bg-card rounded-2xl border border-border shadow-md overflow-hidden hover:shadow-premium transition-shadow duration-300">
        <div ref={ref} className="relative aspect-[4/3] bg-muted overflow-hidden">
          {isInView ? (
            <img
              src={listing.imageUrl}
              alt={listing.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 w-full h-full bg-muted" />
          )}
          {/* Play icon overlay */}
          <Link
            to={`/tour/${listing.slug}`}
            className="absolute inset-0 z-10 flex items-center justify-center bg-foreground/20 opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label={`View 360° tour of ${listing.title}`}
          >
            <div className="gold-gradient shadow-gold flex h-14 w-14 items-center justify-center rounded-full">
              <Play className="ml-0.5 h-6 w-6 text-foreground" />
            </div>
          </Link>
          <span className="absolute top-3 left-3 badge-360 text-xs z-10">360°</span>
        </div>

        <div className="p-5">
          <h3 className="font-display font-bold text-lg text-foreground mb-1 line-clamp-1">
            {listing.title}
          </h3>
          <div className="flex items-center gap-3 text-sm text-muted-foreground mb-3">
            <span className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              {listing.location}
            </span>
            <span className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 fill-secondary text-secondary" />
              {listing.rating}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-display font-bold text-primary">{listing.price}</span>
            <Link to={`/tour/${listing.slug}`}>
              <Button variant="ghost" size="sm" className="text-primary hover:text-primary">
                View Details <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const FeaturedToursSection = () => {
  return (
    <section className="section-padding bg-muted">
      <div className="container-tight">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-primary font-semibold text-sm uppercase tracking-wider"
          >
            Explore Virtually
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display text-3xl sm:text-4xl font-bold text-foreground mt-2"
          >
            Featured 360° Tours
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-muted-foreground mt-3"
          >
            Step inside Nigeria's top venues — no appointment needed.
          </motion.p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {featured360.map((listing, index) => (
            <LazyTourCard key={listing.id} listing={listing} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
